import React, { FC } from 'react';
import { Avatar, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import Medal from './Medal';
import { getCircularAvatar } from '../utils/imageTransform';
import { getFrame, AuthorCosmetics } from '../constants/storeItems';

export interface AvatarUser {
  username: string;
  avatar?: { exists?: boolean; imageLink?: string };
  /** Compact equipped cosmetics from the server; absent when nothing is worn. */
  cos?: AuthorCosmetics;
}

interface UserAvatarProps {
  user: AvatarUser;
  size?: number;
  /** The brand ring PostCard and FeedPreviewCard already draw. */
  ring?: 'brand' | 'none';
  /** DesktopUserMenu uses a rounded avatar; preserved rather than "fixed" here. */
  variant?: 'circular' | 'rounded';
  /** Wrap in a link to the user's profile. */
  link?: boolean;
  /**
   * `auto` paints the user's equipped frame. Nav chrome passes `none` — a frame
   * in the top bar is noise, not status.
   */
  frame?: 'auto' | 'none';
  sx?: Record<string, unknown>;
}

/**
 * One user avatar, everywhere.
 *
 * This existed as 18 hand-built `<Avatar>` pairs across 9 files — every site
 * repeating the "image if set, otherwise the initial" branch at its own size
 * with its own ring. Collapsing it is what makes a purchased avatar frame a
 * one-place change instead of an eighteen-place one.
 *
 * Cosmetics are opt-out (`frame="none"`) rather than opt-in so that a site
 * migrated to this component gets them by default.
 */
const UserAvatar: FC<UserAvatarProps> = ({
  user,
  size = 40,
  ring = 'none',
  variant = 'circular',
  link = false,
  frame = 'auto',
  sx = {},
}) => {
  const theme = useTheme();
  const hasImage = Boolean(user.avatar?.exists && user.avatar.imageLink);
  const frameStops = frame === 'auto' ? getFrame(user.cos?.f)?.stops : undefined;

  // A frame supplies its own edge, so the brand ring would just double it up.
  const ringSx =
    ring === 'brand' && !frameStops
      ? { border: `1px ${theme.tokens.borders.style} ${theme.palette.primary.main}` }
      : {};

  // When framed, the caller's sx (margins, border, shadow) belongs on the frame,
  // not on the photo inside it: a margin inside the ring's fixed box shoves the
  // image off-center. Unframed, the avatar IS the outer element, so it keeps sx.
  const avatarSx = frameStops ? {} : sx;

  const avatar = hasImage ? (
    <Avatar
      src={getCircularAvatar(user.avatar!.imageLink as string, Math.max(160, size * 3))}
      alt={user.username}
      variant={variant}
      sx={{ width: size, height: size, ...ringSx, ...avatarSx }}
    />
  ) : (
    <Avatar
      variant={variant}
      sx={{
        width: size,
        height: size,
        bgcolor: theme.tokens.colors.backgroundCardHover,
        color: theme.tokens.colors.text2,
        fontSize: size * 0.35,
        fontFamily: theme.tokens.typography.sansFontFamily,
        fontWeight: theme.tokens.typography.fontWeightBold,
        ...ringSx,
        ...avatarSx,
      }}
    >
      {user.username?.charAt(0).toUpperCase()}
    </Avatar>
  );

  // The ring adds thickness, so the avatar shrinks to keep the footprint the
  // size the caller asked for — otherwise every framed avatar would nudge its
  // layout wider than an unframed one.
  const framed = frameStops ? (
    <Box sx={{ display: 'inline-flex', flex: 'none', borderRadius: '50%', ...sx }}>
      <Medal
        tier="goat"
        stops={frameStops}
        size={size}
        showTag={false}
        ringWidth={Math.max(2, Math.round(size * 0.06))}
        innerBackground="transparent"
      >
        {React.cloneElement(avatar, {
          sx: { ...(avatar.props.sx || {}), width: '100%', height: '100%' },
        })}
      </Medal>
    </Box>
  ) : (
    avatar
  );

  if (!link) return framed;

  return (
    <Box
      component={RouterLink}
      to={`/u/${user.username}`}
      sx={{ flexShrink: 0, lineHeight: 0, display: 'inline-flex' }}
    >
      {framed}
    </Box>
  );
};

export default UserAvatar;
